//@ts-check
import { COUNTRIES_WITH_FORCED_REDIRECT } from './constants';
import { aloApiFetch } from 'src/scripts/utils/alo-api-fetch';
// * [IMPROVEMENT] rewrite this to be more TS compliant
import { InitialUrlSearchParams } from './environment';
import { retryPromise } from './helpers';

const ipSearchParam = InitialUrlSearchParams.get('ip');
const ipLocationOverrideQueryString = ipSearchParam ? `?ip=${ipSearchParam}` : '';

// `getLocationInfo` add minor indirection between external modules & `locationData` allowing for additional logic
// ex: swap `const` to `let` when all the inital reties fail, 'restart' by re-assigning `locationData` which would be complicated if `locationData` were directly exported
const locationData = retryPromise(
  () =>
    aloApiFetch(`/v1/geoip${ipLocationOverrideQueryString}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Error fetching geoip: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then((data: GeoAloSoftwareLocation) => data),
  3
);

/**
 * backend: custom service / IP based
 */
export const getLocationInfo = () => locationData;

// ? we may want to just name it usZipcodes, less guesswork, but we have IDEs so ??
export const isInGeotarget = async ({
  countries = [],
  usZips = [],
}: { countries?: string[]; usZips?: string[] } = {}) => {
  const locationInfo = await getLocationInfo();
  const { country_code, zip } = locationInfo;

  const countryMatch = countries.includes(country_code); // ? should we also allow continent codes? mostly for EU
  const usZipMatch = country_code === 'US' && usZips.filter((i) => i).some((usZip) => (zip || '').startsWith(usZip));
  return countryMatch || usZipMatch;
};

// prettier-ignore
const eeaCountryCodes = ["AX", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GI", "GR", "GP", "GG", "HU", "IS", "IE", "IM", "IT", "JE", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PL", "PT", "RE", "RO", "SK", "SI", "ES", "SE", "GB", "BL", "KN", "MF", "PM", "GF"];
//-->                                 _____________   A  S  E  A  N    M  e  m  b  e  r  s   _________ & Mexico
const countriesAloBeautyIsBannedIn = ['BN', 'KH', 'TL', 'ID', 'LA', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN', 'MX'];

export const isCodeInEEA = (countryCode: string) => eeaCountryCodes.includes(countryCode);

export const doesCountryBanAloBeauty = (countryCode: string) => countriesAloBeautyIsBannedIn.includes(countryCode);

// Types; // are any of these nullable?
export interface GeoAloSoftwareLocation {
  // status: string;
  // ip: string;
  country_code: string;
  // country_name: string;
  is_eu: boolean;
  // currency: string;
  zip: string;
  // time_zone: string;
  // latitude: number;
  // longitude: number;
  // accuracy_radius: number;
  // metro_code: number;
  // continent: string;
  // city: string;
  // region_name: string;
  region_code: string;
}

// ? [REVISIT] this pattern of Sync & lazy promise evaluation
// ? -- take inspiration from MobX ... or implement react suspense :)

const createGetLocationInfoSync = () => {
  let currentValue: GeoAloSoftwareLocation | null = null;
  return () => {
    getLocationInfo().then((newValue) => (currentValue = newValue));
    return currentValue;
  };
};

// this is only safe because locationInfo is cached long term & is in the browser cache 99% of the time
export const getLocationInfoSync = createGetLocationInfoSync();
getLocationInfoSync();

// ? [REVISIT] to make use of the above
const createIsInGeotargetLazy = ({
  countries,
  usZips,
}: {
  countries?: string[] | undefined;
  usZips?: string[] | undefined;
}) => {
  let currentValue = false;
  return () => {
    isInGeotarget({ countries, usZips }).then((newValue) => (currentValue = newValue));
    return currentValue;
  };
};

export const getIsInUSSync = createIsInGeotargetLazy({ countries: ['US'] });
getIsInUSSync();

export const getIsInUs = async () => await isInGeotarget({ countries: ['US'] });

// ISO 3166-1 alpha-2
// ⚠️ No support for IE 11
export const countryToFlag = (isoCode: string) => {
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    : isoCode;
};

export const conditionallyForceRedirect = (countryCode: string) => {
  if (COUNTRIES_WITH_FORCED_REDIRECT) {
    const countryInfo = COUNTRIES_WITH_FORCED_REDIRECT.find((country) => country.countryCode === countryCode);
    if (countryInfo) {
      const { redirectUrl } = countryInfo;
      const isExternal = !redirectUrl.toLowerCase().startsWith('/') && redirectUrl.includes('.'); // [REVISIT] is that fair to assume that this expression checks for the external(domain) vs internal URL?
      if (isExternal) {
        window.location.href = countryInfo.redirectUrl; // Redirect to dedicated website
        return true;
      }
    }
  }
  return false;
};

export function getCurrentGeoLocationPosition(): Promise<GeolationCoordinatesResponse> {
  return new Promise<GeolationCoordinatesResponse>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position?.coords;
        resolve({
          latitude,
          longitude,
        });
      },
      (error) => reject(error)
    );
  });
}

export const getIntlMinimumShippingThreshold = (currencyCode: string) => {
  const siteSettings = window?.getSiteSettings();
  const intlMinimumShippingThresholdForTopMarkets = siteSettings?.internationalMinimumShippingThreshold[currencyCode];
  const genericIntlShippingThreshold = siteSettings?.shippingThresholdProduct;
  const intlMinimumShippingThreshold = intlMinimumShippingThresholdForTopMarkets ?? genericIntlShippingThreshold;
  return intlMinimumShippingThreshold;
};

interface GeolationCoordinatesResponse {
  latitude: number;
  longitude: number;
}
